/**
 * AppShell — the referee flow's frame, matching the founder-signed-off mock
 * (design-spec/reference/referee-flow.html): warm paper background with the two radial washes,
 * the thread-mark header, a mobile-first single column centred at ~560px on desktop, and the
 * step-progress affordance on the working steps. Every screen (WP-F4–F7) renders inside this.
 */
import type { ReactNode } from 'react';
import type { RefereeContext } from '@/lib/api-types';
import { cn } from '@/lib/utils';
import { isFlowStep, type RefereeStep } from '@/session/steps';
import { stepProgressLabel } from '@/session/progress';
import { ThreadMark } from './ThreadMark';
import { StepProgress } from './StepProgress';

interface AppShellProps {
  step: RefereeStep;
  context?: RefereeContext;
  children: ReactNode;
}

export function AppShell({ step, context, children }: AppShellProps) {
  const onTrack = isFlowStep(step) && stepProgressLabel(step) !== undefined;
  const showContext = isFlowStep(step) && step !== 'landing' && step !== 'done' && !!context;
  // Review is the one step that earns more than the ~560px paper column: only there does a second
  // panel (the transcript, beside the letter) exist to place, and only from `lg` up — the mock
  // allows a side panel at ≥720px, but two columns of dense prose on a tablet is the exact risk
  // the stacked accordion exists to avoid. Every other step stays in the single column.
  const wide = step === 'review';
  const column = wide ? 'max-w-[560px] lg:max-w-[900px]' : 'max-w-[560px]';

  return (
    <div className="referee-shell flex min-h-svh flex-col">
      <header className={cn('mx-auto w-full px-5 pt-[max(18px,env(safe-area-inset-top))]', column)}>
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-2">
            <ThreadMark className="h-5 w-auto" />
            <span className="font-display text-[15px] font-semibold tracking-[-0.01em] text-ink">
              By My Word
            </span>
          </div>
          {showContext && (
            <span className="font-display text-[11px] font-medium text-ink-quiet">
              For {context.candidate.name}
            </span>
          )}
        </div>
        {onTrack && isFlowStep(step) && (
          <div className="pb-3">
            <StepProgress step={step} />
            <span className="sr-only" role="status">
              {stepProgressLabel(step)}
            </span>
          </div>
        )}
      </header>

      {/* keyed so each step re-triggers the CSS mount-in. Lean by design: no animation lib in the
          first-paint path — framer-motion is reserved for the WP-F7 seal ceremony and code-split there. */}
      <main
        key={step}
        className={cn(
          'shell-mount mx-auto flex w-full flex-1 flex-col px-5 pb-[max(24px,env(safe-area-inset-bottom))]',
          column,
        )}
      >
        {children}
      </main>
    </div>
  );
}
